import {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css, type Keyframes } from 'styled-components';

import { animation } from '@/styles/style';

type TransitionProps = {
  status: boolean;
  duration?: number; // ms
  style?: CSSProperties;
  css?: StyledCss;
  animations?: {
    in: Keyframes;
    out: Keyframes;
  };
  disableAnimation?: boolean;
  // fallback must be static node
  fallback?: ReactNode;
  useVerticalScroll?: boolean;
  initailInnerState?: InnerState;
};

type InnerState =
  | 'fallback'
  | 'fallbackOutTran'
  | 'inTran'
  | 'outTran'
  | 'child';

type TransitionStyleProps = {
  $status: boolean;
  $duration?: number;
  $css?: StyledCss;
  $animations?: TransitionProps['animations'];
  $disableAnimation?: boolean;
  $useVerticalScroll?: boolean;
};

const Transition = (
  props: HTMLProps<HTMLDivElement> & TransitionProps,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const outTimer = useRef<NodeJS.Timer | null>(null);
  const inTimer = useRef<NodeJS.Timer | null>(null);
  const fallbackOutTimer = useRef<NodeJS.Timer | null>(null);
  const { status, duration = 200 } = props;
  const innerStateRef = useRef<InnerState>(
    props.initailInnerState || 'fallback',
  );
  const [innerState, setInnerState] = useState<InnerState>(
    props.initailInnerState || 'fallback',
  );

  const setInnerStates = (state: InnerState) => {
    innerStateRef.current = state;
    setInnerState(state);
  };

  useEffect(() => {
    if (outTimer.current) {
      clearTimeout(outTimer.current);
    }
    if (inTimer.current) {
      clearTimeout(inTimer.current);
    }
    if (fallbackOutTimer.current) {
      clearTimeout(fallbackOutTimer.current);
    }
    if (status) {
      if (innerStateRef.current === 'fallback' && props.fallback) {
        setInnerStates('fallbackOutTran');
        fallbackOutTimer.current = setTimeout(() => {
          setInnerStates('inTran');
          inTimer.current = setTimeout(() => {
            setInnerStates('child');
          }, duration);
        }, duration);
      } else {
        setInnerStates('inTran');
        inTimer.current = setTimeout(() => {
          setInnerStates('child');
        }, duration);
      }
    } else {
      if (innerStateRef.current === 'fallback') {
        setInnerStates('fallback');
      } else {
        setInnerStates('outTran');
      }
      outTimer.current = setTimeout(() => {
        setInnerStates('fallback');
      }, duration);
    }
    return () => {
      if (outTimer.current) {
        clearTimeout(outTimer.current);
      }
      if (inTimer.current) {
        clearTimeout(inTimer.current);
      }
      if (fallbackOutTimer.current) {
        clearTimeout(fallbackOutTimer.current);
      }
    };
  }, [duration, props.fallback, status]);

  const isFallback =
    innerState === 'fallback' || innerState === 'fallbackOutTran';

  const computedStatus = isFallback ? !status : status;

  if (props.disableAnimation)
    return (
      <Style
        id={props.id}
        ref={ref}
        key="original"
        $status={computedStatus}
        $duration={duration}
        style={props.style}
        $css={props.css}
        $animations={props.animations}
        className={props.className}
        onClick={props.onClick}
        $disableAnimation={props.disableAnimation}
        $useVerticalScroll={props.useVerticalScroll}
      >
        {!status ? props.fallback : props.children}
      </Style>
    );

  if (isFallback && !props.fallback) return null;

  return (
    <Style
      id={props.id}
      ref={ref}
      key="original"
      $status={computedStatus}
      $duration={duration}
      style={props.style}
      $css={props.css}
      $animations={props.animations}
      className={props.className}
      onClick={props.onClick}
      $disableAnimation={props.disableAnimation}
      $useVerticalScroll={props.useVerticalScroll}
    >
      {isFallback ? props.fallback : props.children}
    </Style>
  );
};

const Style = styled.div<TransitionStyleProps>`
  ${({ $useVerticalScroll: useScroll }) =>
    useScroll &&
    css`
      display: flex;
      flex-direction: column;
      overflow: auto;
      flex: 1;
    `}
  ${({ $disableAnimation, $status, $animations, $duration }) =>
    !$disableAnimation &&
    css`
      animation-name: ${() => {
        const inAnimation = $animations?.in || animation.slideIn;
        const outAnimation = $animations?.out || animation.slideOut;
        return $status ? inAnimation : outAnimation;
      }};
      animation-timing-function: ease-out;
      animation-duration: ${$duration ? `${$duration}ms` : '200ms'};
      animation-fill-mode: both;
    `}
  ${({ $css }) => $css};
`;

export default forwardRef(Transition);
